import React, { useEffect, useState } from "react";
import { Button, Card, Col, DatePicker, Input, Menu, message, Modal, Row, Select, Table, Tag } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { isLoaded, useFirestore } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import urls from "urls";
import { FIRESTORE_CAMPAIGNS_TABLE } from "constants/FirestoreConstant";
import Loading from "components/shared-components/Loading";
import utils from "utils";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";

const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

const CampaignsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const firestore = useFirestore();
  const campaigns = useSelector((state) => state.firestoreReducer.ordered.campaigns);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [dateRange, setDateRange] = useState([]);

  useEffect(() => {
    if (campaigns) {
      setFilteredCampaigns(campaigns);
    }
  }, [campaigns]);

  useEffect(() => {
    filterCampaigns();
  }, [campaigns, searchText, statusFilter, platformFilter, dateRange]);

  const filterCampaigns = () => {
    if (!campaigns) return;

    let filtered = campaigns;

    // Search filter
    if (searchText) {
      filtered = utils.wildCardSearch(filtered, searchText);
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((campaign) => campaign.status === statusFilter);
    }

    // Platform filter
    if (platformFilter !== "all") {
      filtered = filtered.filter((campaign) => campaign.platform === platformFilter);
    }

    // Date range filter
    if (dateRange.length === 2) {
      filtered = filtered.filter((campaign) => {
        if (!campaign.startDate) return false;

        let startDate;
        if (campaign.startDate.toDate && typeof campaign.startDate.toDate === "function") {
          startDate = dayjs(campaign.startDate.toDate());
        } else if (campaign.startDate instanceof Date) {
          startDate = dayjs(campaign.startDate);
        } else {
          startDate = dayjs(campaign.startDate);
        }

        return startDate.isBetween(dateRange[0], dateRange[1], null, "[]");
      });
    }

    setFilteredCampaigns(filtered);
  };

  const handleDelete = async (campaignId) => {
    try {
      await firestore.collection(FIRESTORE_CAMPAIGNS_TABLE).doc(campaignId).delete();
      message.success(t("campaign_deleted"));
    } catch (error) {
      console.error("Error deleting campaign:", error);
      message.error(t("error_deleting_campaign"));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "default",
      active: "success",
      paused: "warning",
      completed: "error",
    };
    return colors[status] || "default";
  };

  const getPlatformColor = (platform) => {
    const colors = {
      facebook_ads: "blue",
      google_ads: "green",
      instagram_ads: "purple",
      email_marketing: "orange",
      other: "default",
    };
    return colors[platform] || "default";
  };

  const dropdownMenu = (record) => (
    <Menu>
      <Menu.Item key="1" onClick={() => navigate(`${urls.editCampaign}/${record.id}`)}>
        <EditOutlined />
        <span style={{ marginLeft: 5 }}>{t("edit")}</span>
      </Menu.Item>
      <Menu.Item
        key="2"
        onClick={() => {
          Modal.confirm({
            title: t("confirm_delete"),
            onOk: () => handleDelete(record.id),
            okText: t("yes"),
            cancelText: t("no"),
          });
        }}
      >
        <DeleteOutlined />
        <span style={{ marginLeft: 5 }}>{t("delete")}</span>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: t("campaign_name"),
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => utils.antdTableSorter(a, b, "name"),
      render: (name, record) => (
        <a
          onClick={() => navigate(`${urls.campaignDetail}/${record.id}`)}
          style={{ cursor: "pointer", fontWeight: 500 }}
        >
          {name}
        </a>
      ),
    },
    {
      title: t("platform"),
      dataIndex: "platform",
      key: "platform",
      render: (platform) => <Tag color={getPlatformColor(platform)}>{t(platform)}</Tag>,
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={getStatusColor(status)}>{t(status)}</Tag>,
    },
    {
      title: t("start_date"),
      dataIndex: "startDate",
      key: "startDate",
      render: (date) => {
        if (!date) return "-";
        // Handle both Firestore Timestamp and regular Date objects
        if (date.toDate && typeof date.toDate === "function") {
          return dayjs(date.toDate()).format("DD-MM-YYYY");
        } else if (date instanceof Date) {
          return dayjs(date).format("DD-MM-YYYY");
        } else if (typeof date === "string" || typeof date === "number") {
          return dayjs(date).format("DD-MM-YYYY");
        }
        return "-";
      },
      sorter: (a, b) => {
        if (!a.startDate && !b.startDate) return 0;
        if (!a.startDate) return -1;
        if (!b.startDate) return 1;

        const getTimestamp = (date) => {
          if (!date) return 0;
          if (date.toDate && typeof date.toDate === "function") {
            return dayjs(date.toDate()).unix();
          } else if (date instanceof Date) {
            return dayjs(date).unix();
          } else {
            return dayjs(date).unix();
          }
        };

        return getTimestamp(a.startDate) - getTimestamp(b.startDate);
      },
    },
    {
      title: t("end_date"),
      dataIndex: "endDate",
      key: "endDate",
      render: (date, record) => {
        if (record.isOngoing) return <Tag color="blue">{t("ongoing")}</Tag>;
        if (!date) return "-";

        // Handle both Firestore Timestamp and regular Date objects
        if (date.toDate && typeof date.toDate === "function") {
          return dayjs(date.toDate()).format("DD-MM-YYYY");
        } else if (date instanceof Date) {
          return dayjs(date).format("DD-MM-YYYY");
        } else if (typeof date === "string" || typeof date === "number") {
          return dayjs(date).format("DD-MM-YYYY");
        }
        return "-";
      },
    },
    {
      title: t("budget"),
      dataIndex: "budget",
      key: "budget",
      render: (budget) => (budget ? `€${budget.toLocaleString()}` : "-"),
      sorter: (a, b) => (a.budget || 0) - (b.budget || 0),
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(record)} />
        </div>
      ),
    },
  ];

  if (!isLoaded(campaigns)) {
    return <Loading cover="content" />;
  }

  return (
    <div>
      <Row justify="end" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(urls.addCampaign)}>
            {t("add_campaign")}
          </Button>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder={t("search_campaigns")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select placeholder={t("status")} value={statusFilter} onChange={setStatusFilter} style={{ width: "100%" }}>
              <Option value="all">{t("all")}</Option>
              <Option value="draft">{t("draft")}</Option>
              <Option value="active">{t("active")}</Option>
              <Option value="paused">{t("paused")}</Option>
              <Option value="completed">{t("completed")}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder={t("platform")}
              value={platformFilter}
              onChange={setPlatformFilter}
              style={{ width: "100%" }}
            >
              <Option value="all">{t("all")}</Option>
              <Option value="facebook_ads">{t("facebook_ads")}</Option>
              <Option value="google_ads">{t("google_ads")}</Option>
              <Option value="instagram_ads">{t("instagram_ads")}</Option>
              <Option value="email_marketing">{t("email_marketing")}</Option>
              <Option value="other">{t("other")}</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [])}
              format="DD-MM-YYYY"
            />
          </Col>
        </Row>

        <Table
          showSorterTooltip={false}
          columns={columns}
          dataSource={filteredCampaigns}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} van ${total} campagnes`,
          }}
        />
      </Card>
    </div>
  );
};

export default CampaignsList;
